import React, { useContext, useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Flex } from 'rebass';
import { ButtonStyled, TextStyled, useTokens } from '@nike/nike-design-system-components';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import TableServerSide from '../../components/tableServerSide/TableServerSide';
import { ActionItemList } from '../common/ActionItemList';
import AddCatalogTypeModal from './AddCatalogTypeModal';
import RemoveCatalogTypeModal from './RemoveCatalogTypeModal';
import SearchSoldTosModal from './SearchSoldTosModal';
import IconMenu from '../../components/IconMenu';

import { getDepartmentColumns } from './departmentColumns';
import { getAccounts } from './selectors';

import useModal from '../../hooks/useModal';
import { getAllCatalogTypes } from './actions';

const DepartmentsList = () => {
  const tokens = useTokens();
  const dispatch = useDispatch();

  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedCatalogType, setSelectedCatalogType] = useState({});
  const [soldToFilterList, setSoldToFilterList] = useState(null);

  const {
    totalResources,
    handleClearSortAndFilters,
    tableInstance: { items: tableData, selectedFlatRows, selectedRowIds = {} },
    refreshTableData
  } = useContext(TableServerSideContext);

  const [isAddCatalogTypeModalOpen, openAddCatalogTypeModal, closeAddCatalogTypeModal] = useModal();
  const [isRemoveCatalogTypeModalOpen, openRemoveCatalogTypeModal, closeRemoveCatalogTypeModal] = useModal();
  const [isSearchSoldTosModal, openSearchSoldTosModal, closeSearchSoldTosModal] = useModal();

  const accountsById = useSelector(getAccounts);

  useEffect(() => {
    setDepartments(tableData);
  }, [JSON.stringify(tableData)]);

  useEffect(() => {
    setSelectedDepartments(selectedFlatRows?.map(item => item.original) || []);
  }, [selectedFlatRows, JSON.stringify(departments)]);

  useEffect(() => {
    if (soldToFilterList !== null) {
      refreshTableData();
    }
  }, [soldToFilterList]);

  useEffect(() => {
    dispatch(getAllCatalogTypes());
  }, []);

  const bulkSetDepartmentCatalogTypes = departmentIdToCatalogTypes => {
    const deptIds = Object.keys(departmentIdToCatalogTypes);
    const departmentsUpdated = departments.map(department => {
      if (deptIds.includes(department.id)) {
        return {
          ...department,
          catalogTypes: departmentIdToCatalogTypes[department.id]
        };
      }
      return department;
    });

    setDepartments(departmentsUpdated);
  };

  const memoizedColumns = useMemo(
    () =>
      getDepartmentColumns({
        departments,
        openAddCatalogTypeModal,
        openRemoveCatalogTypeModal,
        setSelectedDepartments,
        setSelectedCatalogType,
        accountsById
      }),
    [JSON.stringify(departments), JSON.stringify(accountsById)]
  );

  const actionItems = {
    'Add Catalog Type': { action: openAddCatalogTypeModal }
  };

  const selectedSalesOrg =
    selectedDepartments?.length &&
    selectedDepartments.every(
      dept => accountsById[dept.accountId]?.salesOrg === accountsById[selectedDepartments[0].accountId]?.salesOrg
    )
      ? accountsById[selectedDepartments[0].accountId]?.salesOrg
      : null;

  return (
    <Box style={{ backgroundColor: tokens.podiumCdsColorGrey50 }}>
      <Flex
        sx={{
          borderBottom: `1px solid ${tokens.podiumCdsColorGrey200}`,
          padding: '12px',
          width: '100%'
        }}
      >
        <TextStyled appearance='title2'>Departments</TextStyled>
      </Flex>
      <Flex p='12px' width='100%'>
        <Flex alignItems='center' width='75%'>
          <TextStyled appearance='body2' style={{ marginLeft: '5px' }}>
            {`${totalResources}`} results
          </TextStyled>

          <ButtonStyled
            appearance='text'
            background='light'
            ariaLabel='Clear All Filters'
            type='button'
            onClick={() => {
              setSoldToFilterList('');
              handleClearSortAndFilters();
            }}
            style={{ marginLeft: '20px', marginRight: '20px' }}
          >
            Clear All Filters
          </ButtonStyled>

          {selectedRowIds && Object.keys(selectedRowIds)?.length ? (
            <ActionItemList selected={selectedRowIds} actionItems={actionItems} />
          ) : null}

          {soldToFilterList && (
            <ButtonStyled
              appearance='text'
              background='light'
              ariaLabel='Clear Sold-To Filters'
              type='button'
              onClick={() => setSoldToFilterList('')}
              style={{ marginLeft: '20px', marginRight: '20px' }}
            >
              Clear Sold-To Filter
            </ButtonStyled>
          )}
        </Flex>

        <Flex justifyContent='flex-end' width='25%'>
          <IconMenu align='right' menuOptions={[{ text: "Search Sold-To's", onClick: openSearchSoldTosModal }]} />
        </Flex>
      </Flex>

      <Box px='12px'>
        <TableServerSide
          columns={memoizedColumns}
          url='v1/departments'
          extraParams={soldToFilterList ? `&filter=accountId(${soldToFilterList})` : ''}
          itemSize={120}
          fullHeight
          fullWidth
          includeRowSelect
        />
      </Box>

      <AddCatalogTypeModal
        isOpen={isAddCatalogTypeModalOpen}
        closeModal={closeAddCatalogTypeModal}
        salesOrg={selectedSalesOrg}
        allDepartments={departments}
        selectDepartmentIds={selectedDepartments.map(dept => dept.id)}
        // refreshTableData={refreshTableData}
        bulkSetDepartmentCatalogTypes={bulkSetDepartmentCatalogTypes}
      />

      <RemoveCatalogTypeModal
        isOpen={isRemoveCatalogTypeModalOpen}
        closeModal={closeRemoveCatalogTypeModal}
        department={selectedDepartments?.length ? selectedDepartments[0] : {}}
        selectedCatalogType={selectedCatalogType}
        // refreshTableData={refreshTableData}
        bulkSetDepartmentCatalogTypes={bulkSetDepartmentCatalogTypes}
      />

      <SearchSoldTosModal
        title="Search Sold-To's"
        isOpen={isSearchSoldTosModal}
        closeModal={closeSearchSoldTosModal}
        setSoldToFilterList={setSoldToFilterList}
        accounts={Object.values(accountsById)}
      />
    </Box>
  );
};

export default DepartmentsList;
